import { z } from 'zod';
/**
 * The in-app assistant (spec 110 section 4.15, E710, ADR-046): limits, closed
 * value sets and the schemas shared by API and worker. Caps are constants
 * here (rule 7); nothing about a cap lives in a prompt.
 */
export const ASSISTANT_LIMITS = {
    /** Per turn: model rounds, tool calls, write calls and distinct changed items (rule 7). */
    maxModelRounds: 6,
    maxToolCalls: 12,
    maxWriteCalls: 8,
    maxChangedItems: 10,
    /** Per conversation. */
    maxTurns: 20,
    maxToolCallsPerConversation: 150,
    /** Per tenant and day: items changed by the assistant (like `EXTERNAL_TENANT_CAPS.creationsPerDay`, stricter). */
    maxChangedItemsPerDay: 200,
    /** A user message and an assistant answer, in characters. */
    maxTextLength: 2000,
    maxAnswerLength: 4000,
    /** Messages the turn job puts into the model context. */
    historyMessages: 12,
    /** A suggestion chip's length and how many a turn offers. */
    maxSuggestionLength: 40,
    maxSuggestions: 3,
    /** Rule 8 and section 4.15.1: idle expiry, and a running turn counts as stuck after this many minutes. */
    idleDays: 14,
    staleTurnMinutes: 10,
    /** Section 4.15.8: conversations older than this are purged. */
    retentionMonths: 12,
};
/** The origin marker of an assistant turn's `ai_runs` row (module 80's generic `origin`). */
export const ASSISTANT_RUN_ORIGIN = 'assistant';
export const ASSISTANT_CONTEXT_KINDS = ['post', 'campaign', 'calendar', 'media', 'knowledge', 'inbox', 'planning', 'none'];
/** Kinds that name one object and therefore need an `id`. */
export const ASSISTANT_CONTEXT_ID_KINDS = ['post', 'campaign', 'media', 'knowledge', 'inbox'];
export const ASSISTANT_CONVERSATION_STATUSES = ['open', 'closed', 'expired'];
export const ASSISTANT_TURN_STATUSES = ['queued', 'running', 'succeeded', 'failed', 'rejected_budget'];
export const ASSISTANT_TERMINAL_TURN_STATUSES = ['succeeded', 'failed', 'rejected_budget'];
export const ASSISTANT_TURN_ERROR_CODES = ['provider_unavailable', 'schema_validation_failed', 'turn_capped', 'budget_exhausted', 'turn_timeout'];
export const ASSISTANT_PROGRESS_STATES = ['running', 'done', 'failed'];
/** One progress step the panel shows while a turn runs: a tool name and its state, never arguments or results. */
export const assistantProgressStepSchema = z.object({ tool: z.string().min(1).max(64), state: z.enum(ASSISTANT_PROGRESS_STATES), at: z.iso.datetime() }).strict();
/**
 * The page context a client sends (rule 5): IDs and kinds only, nothing else.
 * Strict, so a client-supplied title or text is rejected, never ignored.
 */
export const assistantContextSchema = z
    .object({
    kind: z.enum(ASSISTANT_CONTEXT_KINDS),
    id: z.uuid().optional(),
    property_id: z.uuid().optional(),
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    const needsId = ASSISTANT_CONTEXT_ID_KINDS.includes(value.kind);
    if (needsId && value.id === undefined)
        ctx.addIssue({ code: 'custom', path: ['id'], message: 'required' });
    if (!needsId && value.id !== undefined)
        ctx.addIssue({ code: 'custom', path: ['id'], message: 'not_allowed' });
    if (value.kind === 'none' && (value.property_id !== undefined || value.month !== undefined))
        ctx.addIssue({ code: 'custom', path: ['kind'], message: 'not_allowed' });
});
/** The params of an `assistant_turn` run: IDs and kinds only, no message text (section 4.15.2, AC-110-150). */
export const assistantTurnParamsSchema = z
    .object({
    turn_id: z.uuid(),
    conversation_id: z.uuid(),
    context_kinds: z.array(z.enum(ASSISTANT_CONTEXT_KINDS)).max(1),
})
    .strict();
/** The final answer the model gives (as JSON text inside its last step). */
export const assistantAnswerSchema = z
    .object({
    answer: z.string().min(1).max(ASSISTANT_LIMITS.maxAnswerLength),
    fact_item_ids: z.array(z.uuid()).max(40),
    suggestions: z.array(z.string().min(1).max(ASSISTANT_LIMITS.maxSuggestionLength)).max(ASSISTANT_LIMITS.maxSuggestions),
})
    .strict();
/**
 * One model round (strict for both providers): tool calls with their arguments as JSON text, or the final answer
 * as JSON text. A round may carry several calls (spec 110 section 4.15.2), so the 12 calls of a turn are reachable
 * within its 6 rounds; the limits stay per turn in total. The caps of rule 7 count every call.
 */
export const assistantToolCallSchema = z.object({ tool: z.string().min(1).max(64), arguments: z.string().max(20_000).nullable() }).strict();
export const assistantStepSchema = z
    .object({
    type: z.enum(['tool_call', 'final']),
    calls: z.array(assistantToolCallSchema).max(ASSISTANT_LIMITS.maxToolCalls).nullable(),
    answer: z.string().max(20_000).nullable(),
})
    .strict();
/**
 * What the turn job ends with: the answer plus the loop's counters. Tool call
 * entries hold the tool name, whether it worked and a problem code, never
 * arguments or results. `answer` is empty when the turn was capped.
 */
export const assistantTurnOutputSchema = z
    .object({
    answer: z.string().max(ASSISTANT_LIMITS.maxAnswerLength),
    fact_item_ids: z.array(z.uuid()).max(40),
    suggestions: z.array(z.string().max(ASSISTANT_LIMITS.maxSuggestionLength)).max(ASSISTANT_LIMITS.maxSuggestions),
    tool_calls: z.array(z.object({ tool: z.string().max(64), ok: z.boolean(), problem_code: z.string().max(80).nullable() }).strict()).max(ASSISTANT_LIMITS.maxToolCalls),
    write_calls: z.number().int().min(0).max(ASSISTANT_LIMITS.maxWriteCalls),
    changed_items: z.number().int().min(0).max(ASSISTANT_LIMITS.maxChangedItems),
    capped: z.boolean(),
})
    .strict();
/** The inverse an action carries (section 4.15.1, matrix of section 4.15.4). */
export const ASSISTANT_UNDO_KINDS = ['remove_item', 'archive_campaign', 'restore_version', 'restore_fields', 'remove_localization', 'remove_variant', 'cancel_publication', 'reschedule_back', 'reschedule_group_back', 'none'];
/** The states an action shows, derived from its outcome and undo rows (section 4.15.1). */
export const ASSISTANT_ACTION_STATES = ['pending', 'unknown', 'done', 'undone', 'partially_undone', 'not_undoable'];
/** A named part of an undo; a locale key per part names the one that failed ("Teilweise rueckgaengig"). */
export const ASSISTANT_UNDO_PARTS = ['versioned', 'pillar', 'entries', 'fields', 'item', 'campaign', 'localization', 'variant', 'publication'];
/** The `Idempotency-Key` header an assistant write carries: derived from the action ID (a UUID shaped hash), so a repeat is recognised. */
export const ASSISTANT_ACTION_NAMESPACE = 'assistant-action';
/** Scope of the idempotency records of the assistant's dispatched writes (any route; the key is the action's `idempotency_key`). */
export const ASSISTANT_IDEMPOTENCY_SCOPE = 'assistant.tool';
//# sourceMappingURL=assistant.js.map